import {useUpdatePersonPasswordMutation} from "@ots-frontend/ots-data";
import {useCallback} from "react";
import {ChangePasswordRequest} from "./ChangePasswordDialog";
import {ChangePasswordFieldError} from "./password-policy";

/**
 * Changes the signed-in person's own password through `PUT /person/{id}/password`, for both the
 * profile page and the standalone dialog GWT opens from its Tools menu.
 *
 * Returns a submit handler for `ChangePasswordDialog`, which it rejects the way the dialog expects.
 */
export const useChangeOwnPassword = (personId: string | undefined, onChanged?: () => void) => {
  const [updatePassword] = useUpdatePersonPasswordMutation();

  return useCallback(async ({currentPassword, newPassword}: ChangePasswordRequest) => {
    if (!personId) {
      throw new Error("Your profile hasn't finished loading. Try again in a moment.");
    }

    try {
      await updatePassword({personId, oldPassword: currentPassword, password: newPassword}).unwrap();
    } catch (error) {
      // The server answers a wrong current password with a bare 401 "Authorization Denied" -- the
      // same as an expired session, which it cannot be told apart from. The person was loaded on
      // this session moments ago, so the password is by far the likelier cause.
      if (typeof error === "object" && error !== null && "status" in error && error.status === 401) {
        throw new ChangePasswordFieldError("currentPassword", "That doesn't match your current password.");
      }
      throw error;
    }

    onChanged?.();
  }, [personId, updatePassword, onChanged]);
};
